import type { Node, Path } from 'slate';
import { Editor, Element, Text } from 'slate';

import type { AlineText, AlineTextFormats, LinkElement, NodeMatch } from '../internal';
import {
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isLinkURLEdit,
  Queries,
} from '../internal';

const linkQueries = {
  /**
   * Gets the active URL for a link node, taking into account suggested edits.
   */
  activeLinkURL: (link: LinkElement): string => {
    const linkURLEdit = link.suggestedEdits?.find(isLinkURLEdit);

    if (linkURLEdit) return linkURLEdit.value.href;

    return link.href;
  },

  linkNudgeInlineMatch(editor: Editor): NodeMatch<Node> {
    return (node: Node) =>
      Element.isElement(node) &&
      Editor.isInline(editor, node) &&
      !isCheckboxElement(node) &&
      !isCheckboxOptionFieldElement(node);
  },

  /**
   * Returns formats to be applied to text after removal of a link.
   */
  removeLinkFormats: (editor: Editor, linkPath: Path): Partial<AlineTextFormats> => {
    const paragraphProps = Queries.consistentParagraphProps(editor, { at: linkPath });

    const paragraphVariant = paragraphProps?.variant;
    /**
     * We use the styles from the immediately-prior text node to reset the styles inside the link
     * and, if those styles are absent, the styles of the paragraph that the link is inside.
     */
    const priorTextNodeEntry = Editor.previous<AlineText>(editor, {
      at: linkPath,
      match: Text.isText,
    });

    const styleSheet = Queries.activeStyleSheet(editor);

    const paragraphStyles =
      paragraphVariant && paragraphVariant in styleSheet
        ? styleSheet[paragraphVariant].styles
        : styleSheet['style-normal'].styles;

    const formats: Partial<AlineTextFormats> = {
      fontColor: paragraphStyles.fontColor,
      underline: paragraphStyles.underline,
    };

    if (priorTextNodeEntry) {
      const [priorTextNode] = priorTextNodeEntry;
      formats.fontColor = priorTextNode.fontColor;
      formats.underline = priorTextNode.underline;
    }

    return formats;
  },
};

export default linkQueries;
